import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Checkbox,
  cn,
  Input,
  Label,
  Switch,
  Textarea,
} from '@design-system';
import {
  AdditionalSettingsButton,
  EmailTagInput,
  PrefillAnswersSection,
  useInviteClientStore,
} from '@/features/invite-client';

import {
  AlertTriangle,
  Check,
  Info,
  Link as LinkIcon,
  Loader2,
  Pencil,
  Plus,
  Send,
  Trash2,
  X,
} from 'lucide-react';

import { useState } from 'react';
import { PhoneNumberField } from '@client/components/PhoneNumberField';
import {
  formatByCountry,
  stripDigits,
  validateForCountry,
  type SupportedCountryCode,
} from '@client/util/clerkAuthHelpers';
import type { InviteDeliveryMode, Tag } from '../store/store';
import {
  bulkClientsForEmailOnlyMode,
  emptyBulkClient,
  MAX_BULK_INVITE_CLIENTS,
  useBulkMobileMfaClients,
} from '../hooks/use-bulk-mobile-mfa-clients';

interface EmailTabProps {
  showHeader?: boolean;
  showAdditionalSettings?: boolean;
  showBulkMode?: boolean;
  deliveryMode?: InviteDeliveryMode;
}

export function EmailTab({
  showHeader = true,
  showAdditionalSettings = true,
  showBulkMode = true,
  deliveryMode = 'email',
}: EmailTabProps) {
  const {
    inviteClientConfig,
    isEmailValid,
    isAdvisorEmail,
    canContinueSingle,
    validBulkEmails,
    canContinueBulk,
    isLoading,
    isConsumerSmsMfaAvailable,
    isEmailBodyLocked,
    handleSendEmail,
    handleConfigChange,
    handleClearBulkEmails,
  } = useInviteClientStore();

  const [isEditing, setIsEditing] = useState(false);

  const {
    email,
    firstName,
    lastName,
    emailBody,
    isBulkMode: isBulkModeConfigured,
    bulkEmails,
    isComplianceConfirmed,
    phoneCountryCode,
    phoneNumber,
  } = inviteClientConfig;
  const [touchedPhoneFields, setTouchedPhoneFields] = useState<Set<string>>(new Set());
  const isPrefillDelivery = deliveryMode === 'link';

  // Each invite link belongs to one client and the response carries a single link, so
  // bulk has nothing to return here. The bulk path also posts through batchInviteMutation,
  // which never sets deliveryMode 'link' and would email clients from a link-only flow.
  const allowBulkMode = showBulkMode && !isPrefillDelivery;
  const isBulkMode = allowBulkMode && isBulkModeConfigured;
  const requiresMobileMfa = isConsumerSmsMfaAvailable;
  const emailOnlyBulkEmails = bulkClientsForEmailOnlyMode(bulkEmails);

  const canContinue = isBulkMode ? canContinueBulk : canContinueSingle;

  return (
    <div>
      {showHeader && (
        <div>
          <h4 className="text-base font-semibold text-gray-950">
            {isPrefillDelivery ? 'Create an invite link' : 'Send an email invitation'}
          </h4>
          <p className="text-foreground text-sm">
            {isPrefillDelivery
              ? "We'll create a link for this client that you send to them yourself."
              : "We'll send a proven email template so you don't have to write one from scratch."}
          </p>
        </div>
      )}

      <fieldset
        disabled={isLoading}
        className="mb-6 mt-4"
      >
        <div className="space-y-6">
          {allowBulkMode && (
            <label
              htmlFor="invite-multiple-clients"
              className="bg-muted flex w-full cursor-pointer items-center justify-between rounded-lg border p-3"
            >
              <span className="text-foreground text-sm font-medium">Invite Multiple Clients</span>
              <Switch
                id="invite-multiple-clients"
                checked={isBulkMode}
                onCheckedChange={checked => {
                  handleConfigChange('isBulkMode', !!checked);
                  if (checked && requiresMobileMfa && bulkEmails.length === 0) {
                    handleConfigChange('bulkEmails', [emptyBulkClient()]);
                  }
                }}
              />
            </label>
          )}

          {isBulkMode ? (
            requiresMobileMfa ? (
              <BulkMobileMfaClients
                clients={bulkEmails.length > 0 ? bulkEmails : [emptyBulkClient()]}
                onChange={clients => handleConfigChange('bulkEmails', clients)}
                touchedPhoneFields={touchedPhoneFields}
                onPhoneBlur={key => setTouchedPhoneFields(current => new Set(current).add(key))}
                onClear={() => {
                  handleClearBulkEmails();
                  setTouchedPhoneFields(new Set());
                }}
              />
            ) : (
              <div className="space-y-2">
                <EmailTagInput
                  value={emailOnlyBulkEmails}
                  onChange={value => handleConfigChange('bulkEmails', value)}
                  maxEmails={MAX_BULK_INVITE_CLIENTS}
                />
                {emailOnlyBulkEmails.length > 0 && (
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="border-destructive/10 bg-destructive/5 text-destructive hover:bg-destructive/10 hover:text-destructive px-1.25 h-auto w-fit gap-1 rounded-full border py-0.5 text-xs"
                      onClick={handleClearBulkEmails}
                    >
                      <X className="size-3" />
                      <span className="pr-1">Clear all</span>
                    </Button>
                  </div>
                )}
              </div>
            )
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="firstName">First name (optional)</Label>
                  <Input
                    id="firstName"
                    type="text"
                    placeholder="Jane"
                    value={firstName}
                    onChange={({ target }) => handleConfigChange('firstName', target.value)}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="lastName">Last name (optional)</Label>
                  <Input
                    id="lastName"
                    type="text"
                    placeholder="Doe"
                    value={lastName}
                    onChange={({ target }) => handleConfigChange('lastName', target.value)}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  name="client-email"
                  autoComplete="email"
                  placeholder="client@example.com"
                  value={email}
                  onChange={({ target }) => handleConfigChange('email', target.value)}
                  className={cn({
                    'text-destructive': email.length > 0 && !isEmailValid,
                    'border-yellow-600': isAdvisorEmail,
                  })}
                />
                {isAdvisorEmail && (
                  <Alert variant="warning">
                    <AlertTriangle />
                    <AlertDescription>
                      This email is already associated with your account. Each Waterlily account
                      needs a unique email, so try using a personal email instead.
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              {requiresMobileMfa && (
                <PhoneNumberField
                  id="client-phone"
                  name="client-phone-manual-entry"
                  value={{ countryCode: phoneCountryCode, phoneNumber }}
                  onChange={{
                    countryCode: countryCode => {
                      handleConfigChange('phoneCountryCode', countryCode);
                      handleConfigChange(
                        'phoneNumber',
                        formatByCountry(stripDigits(phoneNumber), countryCode),
                      );
                    },
                    phoneNumber: value =>
                      handleConfigChange(
                        'phoneNumber',
                        formatByCountry(stripDigits(value), phoneCountryCode),
                      ),
                  }}
                  validation={{
                    error: touchedPhoneFields.has('single') ? (
                      <PhoneError
                        phoneNumber={phoneNumber}
                        countryCode={phoneCountryCode}
                      />
                    ) : undefined,
                    invalid:
                      touchedPhoneFields.has('single') &&
                      validateForCountry(stripDigits(phoneNumber), phoneCountryCode) !== null,
                    onBlur: () => setTouchedPhoneFields(current => new Set(current).add('single')),
                  }}
                  options={{ required: true, showHelper: false }}
                />
              )}

              {isPrefillDelivery && <PrefillAnswersSection />}
            </>
          )}

          {/* The template only travels as advisorProvidedBody on the email send, which
              link mode skips, so it would be an editor for text nobody ever receives. */}
          {!isPrefillDelivery && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="cursor-default">Email template</Label>
                {!isEmailBodyLocked && (
                  <Badge
                    className="cursor-pointer"
                    onClick={() => !isLoading && setIsEditing(!isEditing)}
                  >
                    {isEditing ? (
                      <>
                        <Check className="size-3" />
                        <span className="pr-1">Save</span>
                      </>
                    ) : (
                      <>
                        <Pencil className="size-3" />
                        <span className="pr-1">Edit</span>
                      </>
                    )}
                  </Badge>
                )}
              </div>
              {isEmailBodyLocked && (
                <p className="text-muted-foreground text-xs">
                  Your pilot program sets this wording, so it can't be edited.
                </p>
              )}
              {isEditing && !isEmailBodyLocked ? (
                <Textarea
                  value={emailBody}
                  onChange={({ target }) => handleConfigChange('emailBody', target.value)}
                  rows={6}
                  className="text-sm"
                />
              ) : (
                <div className="bg-muted rounded-lg border p-3">
                  <p className="text-foreground line-clamp-3 text-sm">"{emailBody}"</p>
                </div>
              )}
            </div>
          )}

          {isBulkMode && (
            <div className="flex items-start gap-3">
              <Checkbox
                id="compliance"
                checked={isComplianceConfirmed}
                onCheckedChange={checked => handleConfigChange('isComplianceConfirmed', !!checked)}
                className="translate-y-0.5"
              />
              <Label
                htmlFor="compliance"
                className="text-foreground text-sm italic"
              >
                I confirm that I have either obtained compliance approval or understand this message
                may require compliance review. I acknowledge that sending this email is my
                responsibility.
              </Label>
            </div>
          )}
        </div>
      </fieldset>

      <div className="flex gap-3">
        {showAdditionalSettings && <AdditionalSettingsButton className="flex-1" />}
        <Button
          className={showAdditionalSettings ? 'flex-1' : 'w-full'}
          onClick={() => handleSendEmail(isBulkMode, deliveryMode)}
          disabled={!canContinue || isLoading}
        >
          {isLoading ? (
            <Loader2 className="animate-spin" />
          ) : isPrefillDelivery ? (
            <LinkIcon />
          ) : (
            <Send />
          )}
          {isLoading
            ? isPrefillDelivery
              ? 'Creating link...'
              : 'Sending...'
            : isPrefillDelivery
              ? 'Create invite link'
              : isBulkMode
                ? `Send ${validBulkEmails.length} invitation${validBulkEmails.length !== 1 ? 's' : ''}`
                : 'Send invitation'}
        </Button>
      </div>
    </div>
  );
}

function PhoneError({
  phoneNumber,
  countryCode,
}: {
  phoneNumber: string;
  countryCode: SupportedCountryCode;
}) {
  const message = validateForCountry(stripDigits(phoneNumber), countryCode);
  return message ? (
    <p
      role="alert"
      className="text-destructive text-sm"
    >
      {message}
    </p>
  ) : null;
}

function BulkMobileMfaClients({
  clients,
  onChange,
  touchedPhoneFields,
  onPhoneBlur,
  onClear,
}: {
  clients: Tag[];
  onChange: (clients: Tag[]) => void;
  touchedPhoneFields: Set<string>;
  onPhoneBlur: (key: string) => void;
  onClear: () => void;
}) {
  const {
    isPasteOpen,
    openPaste,
    closePaste,
    pasteText,
    setPasteText,
    pasteClientCount,
    pasteReview,
    atLimit,
    updateEmail,
    updateCountryCode,
    updatePhoneNumber,
    removeClient,
    addClient,
    addPastedClients,
    clearClients,
  } = useBulkMobileMfaClients({ clients, onChange, onClear });

  const hasClientInput = clients.some(client => client.value || client.phoneNumber);

  return (
    <div className="space-y-3">
      {pasteReview && (
        <Alert variant="info">
          <Info />
          <AlertTitle>
            {pasteReview.added} {pasteReview.added === 1 ? 'client' : 'clients'} added from your
            list
          </AlertTitle>
          <AlertDescription>
            Review them below.
            {pasteReview.invalid > 0
              ? ` ${pasteReview.invalid} ${pasteReview.invalid === 1 ? 'row needs' : 'rows need'} a fix before ${pasteReview.invalid === 1 ? 'that client' : 'those clients'} can be invited.`
              : ''}
          </AlertDescription>
        </Alert>
      )}

      <div className="flex items-center justify-between gap-3">
        <Label className="cursor-default">Clients</Label>
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground text-xs">
            {clients.length} of {MAX_BULK_INVITE_CLIENTS}
          </span>
          {hasClientInput && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="border-destructive/10 bg-destructive/5 text-destructive hover:bg-destructive/10 hover:text-destructive px-1.25 h-auto w-fit gap-1 rounded-full border py-0.5 text-xs"
              onClick={clearClients}
            >
              <X className="size-3" />
              <span className="pr-1">Clear all</span>
            </Button>
          )}
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border">
        <div className="bg-muted text-muted-foreground hidden grid-cols-[20px_minmax(0,1fr)_minmax(0,1fr)_40px] gap-2 border-b px-2 py-2 pl-3 text-xs sm:grid">
          <span>#</span>
          <span>Email address</span>
          <span>Mobile phone number</span>
          <span />
        </div>

        <div
          data-testid="bulk-mobile-mfa-client-list"
          className="max-h-none divide-y overflow-visible sm:max-h-[280px] sm:overflow-y-auto"
        >
          {clients.map((client, index) => {
            const countryCode = client.phoneCountryCode ?? '+1';
            const phoneKey = `bulk-${index}`;
            const phoneValidationMessage = validateForCountry(
              stripDigits(client.phoneNumber ?? ''),
              countryCode,
            );
            const showPhoneError =
              touchedPhoneFields.has(phoneKey) ||
              client.value.trim().length > 0 ||
              (client.phoneNumber ?? '').length > 0;
            const rowHasError = !!client.error || (showPhoneError && !!phoneValidationMessage);

            return (
              <div
                key={`${client.contactId ?? 'client'}-${index}`}
                className={cn(
                  'relative grid grid-cols-1 gap-3 p-3 sm:grid-cols-[20px_minmax(0,1fr)_minmax(0,1fr)_40px] sm:gap-2 sm:px-2 sm:py-2 sm:pl-3',
                  rowHasError && 'bg-destructive/5',
                )}
              >
                <div className="text-muted-foreground text-sm font-semibold sm:pt-2 sm:text-xs sm:font-normal">
                  <span className="sm:hidden">Client </span>
                  {index + 1}
                </div>
                <div className="space-y-2">
                  <Label
                    htmlFor={`bulk-client-email-${index}`}
                    className="sm:sr-only"
                  >
                    Email address
                  </Label>
                  <Input
                    id={`bulk-client-email-${index}`}
                    type="email"
                    placeholder="client@example.com"
                    value={client.value}
                    aria-invalid={!!client.value && !client.isValid}
                    onChange={event => updateEmail(index, event.target.value)}
                  />
                  {!!client.error && (
                    <p
                      role="alert"
                      className="text-destructive text-sm"
                    >
                      {client.error}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label
                    htmlFor={`bulk-client-phone-${index}`}
                    className="sm:sr-only"
                  >
                    Mobile phone number
                  </Label>
                  <PhoneNumberField
                    id={`bulk-client-phone-${index}`}
                    name={`bulk-client-phone-${index}`}
                    value={{
                      countryCode,
                      phoneNumber: client.phoneNumber ?? '',
                    }}
                    onChange={{
                      countryCode: nextCountryCode =>
                        updateCountryCode(index, nextCountryCode, client.phoneNumber ?? ''),
                      phoneNumber: value => updatePhoneNumber(index, value, countryCode),
                    }}
                    validation={{
                      error: showPhoneError ? (
                        <PhoneError
                          phoneNumber={client.phoneNumber ?? ''}
                          countryCode={countryCode}
                        />
                      ) : undefined,
                      invalid: showPhoneError && !!phoneValidationMessage,
                      onBlur: () => onPhoneBlur(phoneKey),
                    }}
                    options={{
                      required: true,
                      showLabel: false,
                      showHelper: false,
                      compact: true,
                    }}
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-destructive absolute right-2 top-1 sm:static sm:size-10 sm:p-0"
                  aria-label="Remove client"
                  disabled={clients.length === 1}
                  onClick={() => removeClient(index)}
                >
                  <Trash2 />
                  <span className="sm:hidden">Remove</span>
                </Button>
              </div>
            );
          })}
        </div>

        {isPasteOpen && (
          <div className="bg-muted space-y-2 border-t p-3">
            <Label htmlFor="bulk-client-paste">Paste clients</Label>
            <Textarea
              id="bulk-client-paste"
              rows={4}
              className="bg-background text-sm"
              value={pasteText}
              onChange={event => setPasteText(event.target.value)}
            />
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-muted-foreground flex items-center gap-1">
                <Info className="size-3" />
                <p className="text-xs">One client per line: email, phone</p>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={closePaste}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  disabled={pasteClientCount === 0}
                  onClick={addPastedClients}
                >
                  Add {pasteClientCount} {pasteClientCount === 1 ? 'client' : 'clients'}
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col justify-between gap-1 border-t p-1 sm:flex-row">
          <Button
            type="button"
            variant="ghost"
            className="text-primary justify-start"
            disabled={atLimit}
            onClick={addClient}
          >
            <Plus />
            Add another client
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="text-muted-foreground justify-start"
            disabled={atLimit}
            onClick={openPaste}
          >
            Paste a list
          </Button>
        </div>
      </div>

      {atLimit && (
        <Alert variant="warning">
          <AlertTriangle />
          <AlertDescription>Maximum 35 clients allowed</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
